import {
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Stack,
  useToast,
  Box,
  Text,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useState } from 'react';
import { RiUserAddLine, RiMailLine } from 'react-icons/ri';
import { StyledSelect } from '../helivan/StyledSelect';
import {
  OrganizationRole,
  useInviteToOrganizationMutation,
} from '../../generated/graphql';

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  onSuccess?: () => void;
  canInviteAdmin?: boolean;
}

// Subtle gradient animation
const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

export const InviteMemberModal = ({
  isOpen,
  onClose,
  organizationId,
  onSuccess,
  canInviteAdmin = false,
}: InviteMemberModalProps) => {
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrganizationRole>(OrganizationRole.Member);
  const [inviteToOrganization, { loading }] = useInviteToOrganizationMutation();

  const handleSubmit = async () => {
    if (!email.trim()) {
      toast({
        title: 'Email required',
        description: 'Please enter an email address.',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast({
        title: 'Invalid email',
        description: 'Please enter a valid email address.',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      await inviteToOrganization({
        variables: {
          input: {
            organizationId,
            email: email.trim().toLowerCase(),
            role,
          },
        },
      });

      toast({
        title: 'Invitation sent',
        description: `An invitation has been sent to ${email}`,
        status: 'success',
        duration: 3000,
      });

      setEmail('');
      setRole(OrganizationRole.Member);
      onClose();
      onSuccess?.();
    } catch (error) {
      toast({
        title: 'Error sending invitation',
        description: error instanceof Error ? error.message : 'Something went wrong.',
        status: 'error',
        duration: 5000,
      });
    }
  };

  const handleClose = () => {
    setEmail('');
    setRole(OrganizationRole.Member);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered motionPreset="slideInBottom">
      <ModalOverlay
        bg="blackAlpha.600"
        backdropFilter="blur(10px)"
      />
      <ModalContent
        bg="transparent"
        boxShadow="none"
        mx={4}
        overflow="visible"
      >
        {/* Outer glow effect */}
        <Box
          position="absolute"
          top="-2px"
          left="-2px"
          right="-2px"
          bottom="-2px"
          borderRadius="28px"
          bg="linear-gradient(135deg, rgba(0,71,125,0.3) 0%, rgba(72,104,132,0.2) 50%, rgba(0,71,125,0.3) 100%)"
          backgroundSize="200% 200%"
          animation={`${gradientShift} 5s ease infinite`}
          filter="blur(8px)"
          opacity={0.6}
          zIndex={-1}
        />

        {/* Main modal container */}
        <Box
          bg="white"
          borderRadius="24px"
          overflow="hidden"
          boxShadow="0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
        >
          {/* Header with gradient accent */}
          <Box
            position="relative"
            overflow="hidden"
          >
            {/* Gradient bar */}
            <Box
              h="4px"
              bgGradient="linear(to-r, #00477D, #486884, #00477D)"
              backgroundSize="200% 100%"
              animation={`${gradientShift} 3s ease infinite`}
            />

            <Flex
              px={6}
              pt={6}
              pb={4}
              align="center"
              gap={4}
            >
              {/* Icon with gradient background */}
              <Flex
                w="56px"
                h="56px"
                borderRadius="16px"
                bgGradient="linear(135deg, #00477D 0%, #486884 100%)"
                align="center"
                justify="center"
                boxShadow="0 4px 14px rgba(0,71,125,0.3)"
              >
                <RiUserAddLine size={28} color="white" />
              </Flex>

              <Box flex={1}>
                <Text
                  fontSize="20px"
                  fontWeight={700}
                  color="#1a1a2e"
                  letterSpacing="-0.02em"
                >
                  Invite Member
                </Text>
                <Text
                  fontSize="14px"
                  color="#64748b"
                  mt={0.5}
                >
                  Add someone to your organization
                </Text>
              </Box>

              <ModalCloseButton
                position="relative"
                top={0}
                right={0}
                w="36px"
                h="36px"
                borderRadius="12px"
                bg="#f1f5f9"
                color="#64748b"
                _hover={{ bg: '#e2e8f0', color: '#1a1a2e' }}
                transition="all 0.2s"
              />
            </Flex>
          </Box>

          {/* Body */}
          <ModalBody px={6} py={5}>
            <Stack spacing={5}>
              <FormControl isRequired>
                <FormLabel
                  fontSize="13px"
                  fontWeight={600}
                  color="#475569"
                  mb={2}
                  textTransform="uppercase"
                  letterSpacing="0.05em"
                >
                  <Flex align="center" gap={2}>
                    <RiMailLine size={14} color="#475569" />
                    Email Address
                  </Flex>
                </FormLabel>
                <Input
                  type="email"
                  placeholder="colleague@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  size="lg"
                  bg="#f8fafc"
                  border="2px solid"
                  borderColor="#e2e8f0"
                  borderRadius="14px"
                  fontSize="15px"
                  color="#1a1a2e"
                  _placeholder={{ color: '#94a3b8' }}
                  _hover={{ borderColor: '#cbd5e1' }}
                  _focus={{
                    borderColor: '#00477D',
                    boxShadow: '0 0 0 3px rgba(0,71,125,0.1)',
                    bg: 'white',
                  }}
                  transition="all 0.2s"
                />
              </FormControl>

              <FormControl>
                <StyledSelect
                  label="Role"
                  options={[
                    { value: OrganizationRole.Member, label: 'Member' },
                    ...(canInviteAdmin ? [{ value: OrganizationRole.Admin, label: 'Admin' }] : []),
                  ]}
                  value={role}
                  onChange={(value) => setRole(value as OrganizationRole)}
                />
                <Text
                  fontSize="13px"
                  color="#94a3b8"
                  mt={2}
                >
                  {role === OrganizationRole.Admin
                    ? 'Admins can invite members and manage the organization'
                    : 'Members can view and use organization resources'}
                </Text>
              </FormControl>
            </Stack>
          </ModalBody>

          {/* Footer */}
          <Flex
            px={6}
            py={5}
            bg="#f8fafc"
            borderTop="1px solid"
            borderColor="#e2e8f0"
            gap={3}
            justify="flex-end"
          >
            <Box
              as="button"
              px={5}
              py={2.5}
              borderRadius="12px"
              fontSize="14px"
              fontWeight={600}
              color="#64748b"
              bg="white"
              border="1px solid"
              borderColor="#e2e8f0"
              cursor="pointer"
              transition="all 0.2s"
              _hover={{ bg: '#f1f5f9', borderColor: '#cbd5e1', color: '#475569' }}
              _active={{ transform: 'scale(0.98)' }}
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Box>
            <Box
              as="button"
              px={6}
              py={2.5}
              borderRadius="12px"
              fontSize="14px"
              fontWeight={600}
              color="white"
              bgGradient="linear(135deg, #00477D 0%, #1a5a94 100%)"
              cursor={loading || !email.trim() ? 'not-allowed' : 'pointer'}
              opacity={loading || !email.trim() ? 0.6 : 1}
              transition="all 0.2s"
              boxShadow="0 2px 8px rgba(0,71,125,0.3)"
              _hover={!loading && email.trim() ? {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0,71,125,0.4)',
              } : {}}
              _active={!loading && email.trim() ? { transform: 'scale(0.98)' } : {}}
              onClick={!loading && email.trim() ? handleSubmit : undefined}
            >
              {loading ? 'Sending...' : 'Send Invitation'}
            </Box>
          </Flex>
        </Box>
      </ModalContent>
    </Modal>
  );
};
